import {
  ARTIFACT_RENDERERS,
  UnknownArtifactRenderer,
} from "@/components/artifacts/registry";
import {
  CitationBlock,
  NoCitationsBadge,
} from "@/components/artifacts/CitationBlock";
import type { HighlightEntity } from "@/components/artifacts/entityHighlight";
import { hasInlineMarkers } from "@/components/artifacts/inlineCitations";
import type { Artifact, Citation } from "@/api/types";

export interface ArtifactViewProps {
  artifact: Artifact | unknown;
  /**
   * Optional callback invoked when the operator clicks a citation
   * timestamp. Receives the citation's `start_ms` so the parent can
   * scroll the transcript tab to the matching utterance. When omitted,
   * timestamps are still rendered but are non-interactive.
   */
  onJumpToTranscript?: (startMs: number) => void;
  /**
   * Phase 6: entities (bound employees / client) whose display name
   * should be wrapped in a coloured `<mark>` chip inside the artifact
   * body. Empty / undefined → no highlighting.
   */
  highlightEntities?: readonly HighlightEntity[];
}

const KNOWN_TYPES: ReadonlySet<string> = new Set(
  Object.keys(ARTIFACT_RENDERERS),
);

function getArtifactType(value: unknown): string | null {
  if (typeof value !== "object" || value === null) return null;
  const t = (value as { type?: unknown }).type;
  return typeof t === "string" ? t : null;
}

function getCitations(value: unknown): Citation[] | null {
  if (typeof value !== "object" || value === null) return null;
  const c = (value as { citations?: unknown }).citations;
  return Array.isArray(c) ? (c as Citation[]) : null;
}

/**
 * Inspect a known-typed artifact payload and decide whether any of its text
 * carries inline `[[cN]]` markers. We check `body` for text artifacts, every
 * `items[i]` for list artifacts, and every `rows[i][j]` cell for table
 * artifacts. v1.1.5 extended this to tables after operator feedback — without
 * the table branch the legacy bottom CitationBlock kept rendering under tables
 * whose cells already had inline chips (double-attribution).
 */
function hasAnyInlineMarkers(artifact: unknown, type: string): boolean {
  if (typeof artifact !== "object" || artifact === null) return false;
  if (type === "text") {
    const body = (artifact as { body?: unknown }).body;
    return typeof body === "string" && hasInlineMarkers(body);
  }
  if (type === "list") {
    const items = (artifact as { items?: unknown }).items;
    if (!Array.isArray(items)) return false;
    return items.some((it) => typeof it === "string" && hasInlineMarkers(it));
  }
  if (type === "table") {
    const rows = (artifact as { rows?: unknown }).rows;
    if (!Array.isArray(rows)) return false;
    return rows.some(
      (row) =>
        Array.isArray(row) &&
        row.some((cell) => typeof cell === "string" && hasInlineMarkers(cell)),
    );
  }
  return false;
}

/**
 * Generic artifact renderer. Looks up the registry by `artifact.type`; falls
 * back to `<UnknownArtifactRenderer>` for unknown types (LLM drift / new types
 * not yet wired in). Never throws on malformed payloads.
 *
 * Citation rendering (v1.1.4 → operator feedback v1.1.5):
 *
 *  - If the artifact body / items embed `[[cN]]` markers, the per-renderer
 *    `InlineCitedText` puts chips next to the facts they support. The legacy
 *    bottom CitationBlock is suppressed to avoid double-attribution.
 *  - If the payload has `citations[]` but no markers, the bottom CitationBlock
 *    fallback renders (old artifacts created before the marker rollout, or
 *    LLM responses where the marker pass produced none).
 *  - If there are no citations at all, the «без подтверждений» badge sits in
 *    the top-right corner.
 */
export function ArtifactView({
  artifact,
  onJumpToTranscript,
  highlightEntities,
}: ArtifactViewProps) {
  const type = getArtifactType(artifact);
  const citations = getCitations(artifact);
  const knownType = type !== null && KNOWN_TYPES.has(type);

  const body = knownType ? (
    (() => {
      const Renderer =
        ARTIFACT_RENDERERS[type as keyof typeof ARTIFACT_RENDERERS];
      return (
        <Renderer
          artifact={artifact as Artifact}
          onJump={onJumpToTranscript}
          highlightEntities={highlightEntities}
        />
      );
    })()
  ) : (
    <UnknownArtifactRenderer artifact={artifact} />
  );

  // Only known artifact types get the citation footer — Unknown payloads
  // already render their own raw-JSON details and the badge would be noisy.
  if (!knownType) return body;

  const hasCitations = citations !== null && citations.length > 0;
  const inline = hasAnyInlineMarkers(artifact, type as string);

  return (
    <div className="relative" data-testid="artifact-with-citations">
      {!hasCitations && (
        <div className="absolute right-0 top-0">
          <NoCitationsBadge />
        </div>
      )}
      {body}
      {hasCitations && !inline && (
        <CitationBlock
          citations={citations as Citation[]}
          onJump={onJumpToTranscript}
        />
      )}
    </div>
  );
}
